import React from 'react';
import { Button } from '../ui/button';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { ArrowRight, Play, Sparkles, Brain, Calendar, Star, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';

const Hero = ({ onOpenAnalysis, onOpenConsultation }) => {
  const { heroContent, stats, features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const showAIButton = onOpenAnalysis && features?.showAIAnalysis !== false;
  
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className={`relative min-h-screen flex items-center justify-center overflow-hidden transition-colors duration-500 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-gradient-to-b from-gray-50 to-white'
    }`}>
      {/* Simple Background - Safari optimized */}
      <div className="absolute inset-0">
        {/* Static gradient background instead of animated blurs */}
        <div className={`absolute inset-0 ${
          isDark 
            ? 'bg-gradient-to-br from-[#7BA7C2]/10 via-transparent to-[#5A8BA6]/10' 
            : 'bg-gradient-to-br from-[#7BA7C2]/5 via-transparent to-[#5A8BA6]/5'
        }`} />
        
        {/* Grid Pattern */}
        <div 
          className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.04]'}`}
          style={{
            backgroundImage: `linear-gradient(${isDark ? '#7BA7C2' : '#5A8BA6'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? '#7BA7C2' : '#5A8BA6'} 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
        
        {/* Floating Particles - reduced for Safari performance */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full ${
              isDark ? 'bg-[#7BA7C2]/40' : 'bg-[#5A8BA6]/30'
            }`}
            style={{
              left: `${20 + i * 15}%`,
              top: `${20 + i * 12}%`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center flex flex-col items-center">
          {/* Logo */}
          <div className="mb-8 animate-fade-in">
            <img 
              src="https://customer-assets.emergentagent.com/job_dion-hair/artifacts/cm40t14d_DionHairClinic_Logo.svg"
              alt="Dion Hair Clinic - Haartransplantation Mönchengladbach"
              width="256"
              height="80"
              fetchPriority="high"
              className={`h-20 sm:h-24 md:h-32 w-auto transition-all duration-300 ${
                isDark ? 'brightness-0 invert' : ''
              }`}
            />
          </div>

          {/* Main Heading */}
          <h1 className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            <span className="block animate-slide-up">
              {language === 'de' ? heroContent.title : (
                features?.analysisType === 'beard' ? 'Beard Transplant' :
                features?.analysisType === 'eyebrow' ? 'Eyebrow Transplant' :
                'Natural Hair Transplant'
              )}
            </span>
            <span 
              className="block mt-2 bg-gradient-to-r from-[#7BA7C2] via-[#9BBFD9] to-[#7BA7C2] bg-clip-text text-transparent animate-slide-up bg-[length:200%_auto] animate-gradient"
              style={{ animationDelay: '0.1s' }}
            >
              {language === 'de' ? heroContent.subtitle : (
                features?.analysisType === 'beard' ? 'Full Beard, Naturally' :
                features?.analysisType === 'eyebrow' ? 'Natural Beauty, Permanent' :
                'in Germany'
              )}
            </span>
          </h1>

          {/* Description */}
          <p 
            className={`max-w-2xl mx-auto text-lg sm:text-xl mb-10 leading-relaxed animate-slide-up ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}
            style={{ animationDelay: '0.2s' }}
          >
            {language === 'de' ? heroContent.description : (
              features?.analysisType === 'beard' 
                ? 'Full, dense beard - permanent and natural. Precise transplantation for masculine results.'
                : features?.analysisType === 'eyebrow'
                ? 'Full, perfectly shaped eyebrows - the permanent alternative to microblading. Precise transplantation for natural results.'
                : 'State-of-the-art FUE and DHI technology, experienced specialists, and natural-looking results. Dion Hair Clinic - Your first choice for hair transplantation.'
            )}
          </p>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-slide-up"
            style={{ animationDelay: '0.3s' }}
          >
            {showAIButton ? (
              <>
                <Button
                  onClick={onOpenAnalysis}
                  className="group relative px-8 py-6 text-lg bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white border-0 shadow-2xl shadow-[#7BA7C2]/30 hover:shadow-[#7BA7C2]/50 transition-all duration-500 overflow-hidden btn-shine"
                  data-testid="hero-analysis-btn"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <Brain className="w-5 h-5" />
                    {t('nav.freeAiAnalysis')}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#9BBFD9] to-[#7BA7C2] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </Button>
                <Button
                  onClick={onOpenConsultation}
                  variant="outline"
                  className={`group px-8 py-6 text-lg transition-all duration-300 ${
                    isDark 
                      ? 'border-[#7BA7C2]/30 text-white hover:bg-[#7BA7C2]/10 hover:border-[#7BA7C2]/50' 
                      : 'border-[#5A8BA6]/40 text-gray-800 hover:bg-[#5A8BA6]/10 hover:border-[#5A8BA6]/60'
                  }`}
                >
                  <Calendar className={`w-5 h-5 mr-2 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                  {t('nav.freeConsultation')}
                </Button>
              </>
            ) : (
              <>
              <Button
                onClick={onOpenConsultation}
                className="group relative px-8 py-6 text-lg bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white border-0 shadow-2xl shadow-[#7BA7C2]/30 hover:shadow-[#7BA7C2]/50 transition-all duration-500 overflow-hidden btn-shine"
                data-testid="hero-cta-btn"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {t('nav.freeConsultation')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#9BBFD9] to-[#7BA7C2] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </Button>
              <Link to="/preisrechner">
                <Button
                  variant="outline"
                  className={`group px-8 py-6 text-lg transition-all duration-300 ${
                    isDark 
                      ? 'border-[#7BA7C2]/30 text-white hover:bg-[#7BA7C2]/10 hover:border-[#7BA7C2]/50' 
                      : 'border-[#5A8BA6]/40 text-gray-800 hover:bg-[#5A8BA6]/10 hover:border-[#5A8BA6]/60'
                  }`}
                  data-testid="hero-price-calculator-btn"
                >
                  <Calculator className={`w-5 h-5 mr-2 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                  {language === 'de' ? 'Kosten berechnen' : 'Calculate Costs'}
                </Button>
              </Link>
              </>
            )}
          </div>

          {/* Stats */}
          <div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto animate-slide-up"
            style={{ animationDelay: '0.4s' }}
          >
            {stats.map((stat, index) => {
              // Check if this is the Google rating stat
              const isGoogleRating = stat.label.toLowerCase().includes('google');
              const displayValue = isGoogleRating ? stat.value.replace('★', '') : stat.value;
              
              return (
                <div 
                  key={index} 
                  className={`group relative p-6 rounded-2xl transition-all duration-500 ${
                    isDark 
                      ? 'bg-white/[0.02] border border-white/[0.05] hover:border-[#7BA7C2]/30 hover:bg-[#7BA7C2]/5' 
                      : 'bg-white border border-gray-200 hover:border-[#5A8BA6]/30'
                  }`}
                >
                  <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                    isDark ? 'from-[#7BA7C2]/10 to-transparent' : 'from-[#5A8BA6]/5 to-transparent'
                  }`} />
                  <div className="relative">
                    <div className={`text-3xl sm:text-4xl font-bold mb-1 transition-colors duration-300 flex items-center justify-center gap-1 ${
                      isDark 
                        ? 'text-white group-hover:text-[#7BA7C2]' 
                        : 'text-gray-900 group-hover:text-[#5A8BA6]'
                    }`}>
                      {displayValue}
                      {isGoogleRating && (
                        <Star className="w-7 h-7 text-yellow-500 fill-yellow-500" />
                      )}
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${
                      isDark 
                        ? 'text-gray-500 group-hover:text-gray-400' 
                        : 'text-gray-500 group-hover:text-gray-600'
                    }`}>
                      {language === 'de' ? stat.label : (
                        stat.label.includes('Erfolgreiche') ? 'Successful Treatments' :
                        stat.label.includes('DHI Behandlungen') ? 'DHI Treatments' :
                        stat.label.includes('Ohne Rasur') ? 'No Shave' :
                        stat.label.includes('Jahre') ? 'Years Experience' :
                        stat.label.includes('Zufriedene') ? 'Satisfied Patients' :
                        stat.label.includes('Google') ? 'Google Rating' :
                        stat.label
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
        <div className={`w-6 h-10 rounded-full border-2 flex items-start justify-center p-2 ${
          isDark ? 'border-[#7BA7C2]/30' : 'border-[#5A8BA6]/40'
        }`}>
          <div className={`w-1 h-2 rounded-full animate-scroll-down ${
            isDark ? 'bg-[#7BA7C2]' : 'bg-[#5A8BA6]'
          }`} />
        </div>
      </div>
    </section>
  );
};

export default Hero;
